import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Dimensions, PanResponder } from 'react-native';
import { AreaChart, LineChart } from 'react-native-svg-charts';
import Svg, { Defs, LinearGradient, Stop, Circle } from 'react-native-svg';
import * as shape from 'd3-shape';
import { useTheme } from '../styles/ThemeProvider';

// Chart positioning constants
const CHART_TOP_INSET = 20;
const CHART_DRAWABLE_HEIGHT = 160; // Total height (220) - top inset (20) - bottom inset (40)

interface PortfolioSnapshot {
  snapshot_date: string;
  total_value: number;
  item_count: number;
}

type TimePeriod = '1W' | '1M' | '3M' | 'YTD' | 'All';

interface Props {
  data: PortfolioSnapshot[];
  days?: number;
  totalValue: number;
}

export default function PortfolioChart({ data, days = 30, totalValue }: Props) {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          paddingTop: 0,
          paddingHorizontal: theme.spacing.lg,
          paddingBottom: 0,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        changeInfo: {
          alignItems: 'flex-start',
          marginBottom: theme.spacing.sm,
          paddingLeft: theme.spacing.sm,
        },
        changeText: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
        },
        timePeriod: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
        },
        changePositive: {
          color: theme.colors.trendUp,
        },
        changeNegative: {
          color: theme.colors.trendDown,
        },
        chartContainer: {
          marginLeft: -theme.spacing.lg,
          marginRight: -theme.spacing.lg,
          marginBottom: theme.spacing.sm,
        },
        emptyState: {
          padding: theme.spacing.xxl,
          alignItems: 'center',
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          marginTop: theme.spacing.sm,
        },
        emptyText: {
          ...theme.typography.md,
          color: theme.colors.textPrimary,
          marginBottom: theme.spacing.xs,
        },
        emptySubtext: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          textAlign: 'center',
        },
        periodSelector: {
          flexDirection: 'row',
          justifyContent: 'space-around',
          marginTop: 0,
          marginBottom: theme.spacing.lg,
          paddingHorizontal: theme.spacing.sm,
        },
        periodButton: {
          paddingVertical: theme.spacing.xs,
          paddingHorizontal: theme.spacing.md,
          borderRadius: theme.radius.radiusLg,
          backgroundColor: 'transparent',
        },
        periodButtonActive: {
          backgroundColor: theme.colors.accent,
        },
        periodButtonText: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textSecondary,
        },
        periodButtonTextActive: {
          color: theme.colors.backgroundPrimary,
          fontFamily: theme.fontFamily.bodyMedium,
        },
        tooltip: {
          position: 'absolute',
          backgroundColor: theme.colors.backgroundElevated,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          paddingVertical: theme.spacing.sm,
          paddingHorizontal: theme.spacing.md,
          borderRadius: theme.radius.radiusMd,
          minWidth: 150,
          alignItems: 'center',
          ...theme.shadows.cardShadow,
        },
        tooltipDate: {
          ...theme.typography.xs,
          color: theme.colors.textSecondary,
        },
        tooltipValue: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
          marginTop: theme.spacing.xxs,
        },
      }),
    [theme],
  );
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('1M');
  const [selectedPoint, setSelectedPoint] = useState<{ x: number; y: number; date: Date } | null>(null);

  if (!data || data.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>Not enough data yet</Text>
          <Text style={styles.emptySubtext}>Check back tomorrow to see your portfolio trend</Text>
        </View>
      </View>
    );
  }

  // Filter data based on selected period and fill gaps at start and end
  const filterDataByPeriod = (period: TimePeriod) => {
    const now = new Date();
    now.setHours(23, 59, 59, 999); // End of today

    const sorted = [...data].sort((a, b) =>
      new Date(a.snapshot_date).getTime() - new Date(b.snapshot_date).getTime()
    );

    let startDate: Date;
    switch (period) {
      case '1W':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 6); // 6 days ago + today = 7 days
        startDate.setHours(0, 0, 0, 0); // Start of day
        break;
      case '1M':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29); // 29 days ago + today = 30 days
        startDate.setHours(0, 0, 0, 0);
        break;
      case '3M':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 89); // 89 days ago + today = 90 days
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'YTD':
        startDate = new Date(now.getFullYear(), 0, 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'All':
      default:
        return sorted;
    }

    const filtered = sorted.filter(d => new Date(d.snapshot_date) >= startDate);

    if (filtered.length === 0) return [];

    const result: PortfolioSnapshot[] = [];
    const firstDataPoint = filtered[0];
    const firstDate = new Date(firstDataPoint.snapshot_date);

    // Backfill from startDate to first data point with first known value
    const daysBeforeFirst = Math.floor((firstDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    if (daysBeforeFirst > 0) {
      for (let i = 0; i < daysBeforeFirst; i++) {
        const fillDate = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
        result.push({
          snapshot_date: fillDate.toISOString(),
          total_value: firstDataPoint.total_value,
          item_count: firstDataPoint.item_count,
        });
      }
    }

    // Add actual data
    result.push(...filtered);

    // Forward fill from last data point to now
    const lastDataPoint = filtered[filtered.length - 1];
    const lastDate = new Date(lastDataPoint.snapshot_date);
    const daysAfterLast = Math.floor((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));

    // Normalize today's date to midnight for comparison
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    if (daysAfterLast > 0) {
      for (let i = 1; i <= daysAfterLast; i++) {
        const fillDate = new Date(lastDate.getTime() + i * 24 * 60 * 60 * 1000);
        // Normalize fillDate to midnight for comparison
        const fillDateMidnight = new Date(fillDate);
        fillDateMidnight.setHours(0, 0, 0, 0);
        const isToday = fillDateMidnight.getTime() === today.getTime();
        result.push({
          snapshot_date: fillDate.toISOString(),
          // Use current totalValue for today, otherwise use last snapshot value
          total_value: isToday ? totalValue : lastDataPoint.total_value,
          item_count: lastDataPoint.item_count,
        });
      }
    }

    return result;
  };

  // Prepare chart data
  const sortedData = filterDataByPeriod(selectedPeriod);

  // Handle empty data for selected period
  if (sortedData.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.changeInfo}>
          <Text style={styles.changeText}>No data available for this period</Text>
        </View>
        <View style={styles.periodSelector}>
          {(['1W', '1M', '3M', 'YTD', 'All'] as TimePeriod[]).map((period) => (
            <TouchableOpacity
              key={period}
              style={[
                styles.periodButton,
                selectedPeriod === period && styles.periodButtonActive,
              ]}
              onPress={() => setSelectedPeriod(period)}
            >
              <Text style={[
                styles.periodButtonText,
                selectedPeriod === period && styles.periodButtonTextActive,
              ]}>
                {period}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    );
  }

  // Prepare data for chart
  const chartData = sortedData
    .filter(d => d.total_value > 0)
    .map((d, index) => ({
      x: index,
      y: d.total_value,
      date: new Date(d.snapshot_date),
    }));

  // Calculate change from first to last value
  const firstValue = chartData[0]?.y || 0;
  const lastValue = chartData[chartData.length - 1]?.y || 0;
  const changeAmount = lastValue - firstValue;
  const changePercent = firstValue > 0 ? ((changeAmount / firstValue) * 100) : 0;

  // Up = trendUp (green), down = trendDown (orange).
  const chartColor = changeAmount >= 0 ? theme.colors.trendUp : theme.colors.trendDown;
  const screenWidth = Dimensions.get('window').width;

  // Get period label
  const getPeriodLabel = (period: TimePeriod): string => {
    switch (period) {
      case '1W': return 'Past week';
      case '1M': return 'Past month';
      case '3M': return 'Past 3 months';
      case 'YTD': return 'Year to date';
      case 'All': return 'All time';
      default: return '';
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.changeInfo}>
        <Text style={[
          styles.changeText,
          changeAmount >= 0 ? styles.changePositive : styles.changeNegative,
        ]}>
          {changeAmount >= 0 ? '+' : ''}${Math.abs(changeAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({changePercent >= 0 ? '+' : ''}{changePercent.toFixed(2)}%) <Text style={styles.timePeriod}>{getPeriodLabel(selectedPeriod)}</Text>
        </Text>
      </View>

      <View
        style={[styles.chartContainer, { overflow: 'hidden' }]}
        {...PanResponder.create({
          onStartShouldSetPanResponder: () => true,
          onMoveShouldSetPanResponder: () => true,
          onPanResponderGrant: (evt) => {
            const x = evt.nativeEvent.locationX;
            const index = Math.round((x / screenWidth) * (chartData.length - 1));
            const clampedIndex = Math.max(0, Math.min(chartData.length - 1, index));
            if (chartData[clampedIndex]) {
              setSelectedPoint(chartData[clampedIndex]);
            }
          },
          onPanResponderMove: (evt) => {
            const x = evt.nativeEvent.locationX;
            const index = Math.round((x / screenWidth) * (chartData.length - 1));
            const clampedIndex = Math.max(0, Math.min(chartData.length - 1, index));
            if (chartData[clampedIndex]) {
              setSelectedPoint(chartData[clampedIndex]);
            }
          },
          onPanResponderRelease: () => {
            // Keep the selected point visible after release
            // It will only change when another point is selected
          },
        }).panHandlers}
      >
        <AreaChart
          style={{ height: 220 }}
          data={chartData.map(d => d.y)}
          contentInset={{ top: 20, bottom: 40, left: 0, right: 0 }}
          curve={shape.curveMonotoneX}
          svg={{ fill: 'url(#gradient)' }}
          gridMin={0}
          yMin={0}
        >
          <Defs key={'gradient'}>
            <LinearGradient id={'gradient'} x1={'0%'} y1={'0%'} x2={'0%'} y2={'100%'}>
              <Stop offset={'0%'} stopColor={chartColor} stopOpacity={0.25} />
              <Stop offset={'100%'} stopColor={chartColor} stopOpacity={0.02} />
            </LinearGradient>
          </Defs>
        </AreaChart>

        <LineChart
          style={{ height: 220, position: 'absolute', top: 0, left: 0, right: 0 }}
          data={chartData.map(d => d.y)}
          contentInset={{ top: 20, bottom: 40, left: 0, right: 0 }}
          curve={shape.curveMonotoneX}
          svg={{
            stroke: chartColor,
            strokeWidth: 2,
          }}
          gridMin={0}
          yMin={0}
        />

        {selectedPoint && (() => {
          const pointIndex = chartData.findIndex(d => d.x === selectedPoint.x);

          // Calculate positions - must match chart's yMin={0} and gridMin={0}
          const yValues = chartData.map(d => d.y);
          const minY = 0; // Chart starts at 0 due to yMin={0}
          const maxY = Math.max(...yValues);
          const yRange = maxY - minY || 1; // Range from 0 to max

          const xPosition = (pointIndex / Math.max(chartData.length - 1, 1)) * screenWidth;
          // Map value to y position (inverted, with insets)
          const yPosition = CHART_TOP_INSET + ((maxY - selectedPoint.y) / yRange) * CHART_DRAWABLE_HEIGHT;

          return (
            <Svg
              style={{ height: 220, width: screenWidth, position: 'absolute', top: 0, left: 0 }}
              pointerEvents="none"
            >
              <Circle
                cx={xPosition}
                cy={yPosition}
                r={6}
                stroke={chartColor}
                strokeWidth={2}
                fill={theme.colors.backgroundPrimary}
              />
            </Svg>
          );
        })()}

        {selectedPoint && (() => {
          const pointIndex = chartData.findIndex(d => d.x === selectedPoint.x);
          const xPosition = (pointIndex / Math.max(chartData.length - 1, 1)) * screenWidth;

          // Determine if tooltip should be on left or right side of point
          const tooltipWidth = 150;
          const isLeftSide = xPosition > screenWidth / 2;

          return (
            <View style={[
              styles.tooltip,
              {
                left: isLeftSide ? xPosition - tooltipWidth - 20 : xPosition + 20,
                top: 40,
              },
            ]}>
              <Text style={styles.tooltipDate}>
                {selectedPoint.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </Text>
              <Text style={styles.tooltipValue}>
                ${selectedPoint.y.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Text>
            </View>
          );
        })()}
      </View>

      {/* Time Period Selector */}
      <View style={styles.periodSelector}>
        {(['1W', '1M', '3M', 'YTD', 'All'] as TimePeriod[]).map((period) => (
          <TouchableOpacity
            key={period}
            style={[
              styles.periodButton,
              selectedPeriod === period && styles.periodButtonActive,
            ]}
            onPress={() => {
              setSelectedPeriod(period);
              setSelectedPoint(null); // Clear tooltip when changing periods
            }}
          >
            <Text style={[
              styles.periodButtonText,
              selectedPeriod === period && styles.periodButtonTextActive,
            ]}>
              {period}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
}

